import { inject, Injectable } from '@angular/core';
import { RouteService } from './route.service';
import { RestService } from './rest.service';
import { environment } from '../../environments/environment';
import { AccessCode, BookAccessCodeTypes, UserData } from '../models/user-data';
import { ApplicationStateService } from './application-state.service';
import { AccessService } from './access.service';

@Injectable({
    providedIn: 'root'
})
export class UserDataService {
    accessService = inject(AccessService);

    constructor(private routeService: RouteService,
                private restService: RestService,
                private applicationStateService: ApplicationStateService) {
    }

    /**
     * Initialize the user data for the current user.
     *
     * @returns {Promise<UserData>}
     */
    async init(): Promise<UserData> {
        if (this.applicationStateService.userData) {
            return this.applicationStateService.userData;
        }

        let route = this.routeService.getRelativeRoute(environment.endpointUrls.userData, {
            bookId: this.applicationStateService.bookId
        });

        this.applicationStateService.userData = await this.restService.get(route);
        this.applicationStateService.userData.accessCodeSelected = true;

        if (environment.debug && new URLSearchParams(window.location.search).get('unsetBAC') !== null) {
            this.applicationStateService.userData.bookAccessCode = undefined;
        }

        if (this.hasBookAccessCode()) {
            await this.getUserAccessCodes();
        }

        route = this.routeService.getRelativeRoute(environment.endpointUrls.userPermissions, {});
        this.applicationStateService.userData.permissions = await this.restService.get(route);

        return this.applicationStateService.userData;
    }

    /**
     * Fetch all user access codes related to the current book.
     */
    async getUserAccessCodes(): Promise<void> {
        const route = this.routeService.getRelativeRoute(environment.endpointUrls.userBookAccessCodes, {});

        try {
            const result = await this.restService.post(route, { isbn: this.applicationStateService.bookConfig.isbn });

            this.applicationStateService.userData.accessCodes = result.codes;
            this.applicationStateService.userData.accessCodeSelected = this.applicationStateService.userData.accessCodes.length <= 1;

            if (result.selected) {
                this.applicationStateService.userData.bookAccessCode = {
                    ...result.selected,
                    quickLink: result.selected.quickLink,
                    isbn: null,
                    version: result.selected.version,
                    hash: result.selected.hash,
                    pin: result.selected.pin,
                    type: BookAccessCodeTypes[result.selected.type],
                    userId: this.applicationStateService.userData.bookAccessCode.userId,
                    bookId: this.applicationStateService.userData.bookAccessCode.bookId,
                    canUpgrade: result.selected.canUpgrade
                };

                this.applicationStateService.userData.accessCodeSelected = true;
            }
        } catch (error) {
            this.applicationStateService.userData.accessCodes = [];
            this.applicationStateService.userData.accessCodeSelected = true;
        }
    }

    /**
     * Determine if user has a better redeemed code for this book.
     */
    hasDifferentPremiumCode(): boolean {
        if (!this.hasBookAccessCode() || this.isPlusFeatureAccessible()) {
            return false;
        }

        if (!this.applicationStateService.userData.accessCodes || this.applicationStateService.userData.accessCodes.length === 1) {
            return false;
        }

        let codeFound = false;
        this.applicationStateService.userData.accessCodes.forEach((code: AccessCode) => {
            if (code.code === this.applicationStateService.userData.bookAccessCode.quickLink) {
                return;
            }

            if (this.accessService.isPlus(code.version)) {
                codeFound = true;

                return false;
            }
        });

        return codeFound;
    }

    /**
     * Update used counter for chosen code. Set selected code for external requests.
     */
    async useAccessCode(code: AccessCode): Promise<any> {
        const route = this.routeService.getRelativeRoute(environment.endpointUrls.userUseAccessCode, {});

        return this.restService
            .post(route, { code: code.code, resource: this.applicationStateService.bookConfig.isbn })
            .then(() => {
                this.applicationStateService.userData.accessCodeSelected = true;
                this.applicationStateService.userData.bookAccessCode = {
                    ...code,
                    pin: code.pin,
                    quickLink: code.code,
                    isbn: null,
                    version: code.version,
                    hash: code.hash,
                    type: BookAccessCodeTypes[code.type],
                    userId: this.applicationStateService.userData.bookAccessCode.userId,
                    bookId: this.applicationStateService.userData.bookAccessCode.bookId,
                    canUpgrade: code.canUpgrade
                };
            });
    }

    /**
     * Tells if Book is a Digi4School Book
     * @returns {boolean}
     */
    isDigi4SchoolBook(): boolean {
        if (!this.applicationStateService.userData) {
            return false;
        }

        const digi4SchoolCode = this.applicationStateService.userData.digi4SchoolCode;

        return digi4SchoolCode !== undefined && digi4SchoolCode !== null;
    }

    /**
     * Determine if user has a book access code.
     */
    hasBookAccessCode(): boolean {
        if (!this.applicationStateService.userData) {
            return false;
        }

        return this.applicationStateService.userData.bookAccessCode !== undefined &&
            this.applicationStateService.userData.bookAccessCode !== null;
    }

    /**
     * Determine if all plus features are available.
     */
    isPlusFeatureAccessible(): boolean {
        if (!this.applicationStateService.userData) {
            return false;
        }

        if (!this.applicationStateService.userData.bookAccessCode) {
            return false;
        }

        return this.accessService.isPlus(this.applicationStateService.userData.bookAccessCode.version);
    }

    /**
     * Determine if all plus features are available.
     */
    isNormalFeatureAccessible(): boolean {
        if (!this.applicationStateService.userData) {
            return false;
        }

        if (!this.applicationStateService.userData.bookAccessCode) {
            return false;
        }

        return this.accessService.isNormal(this.applicationStateService.userData.bookAccessCode.version);
    }

    /**
     * Start trial period for the given code.
     */
    async startTrial(code: string): Promise<any> {
        const route = this.routeService.getRelativeRoute(environment.endpointUrls.startTrial, {});

        return this.restService.post(route, { code });
    }
}
